import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    IconButton,
    CircularProgress,
    Alert,
    Divider
} from '@mui/material';
import {
    Inventory as PackingIcon,
    History as HistoryIcon,
    Label as LabelIcon,
    CheckCircle as FinalizeIcon,
    Calculate as CalcIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { packingApi, PackingBatchReady, PackingBatchItem, PackingCreate } from '../../api/packing';
import { useSnackbar } from 'notistack';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div hidden={value !== index} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

export default function PackingLabeling() {
    const [tabValue, setTabValue] = useState(0);
    const [readyBatches, setReadyBatches] = useState<PackingBatchReady[]>([]);
    const [history, setHistory] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { enqueueSnackbar } = useSnackbar();

    // Packing Record Form State
    const [openPackingDialog, setOpenPackingDialog] = useState(false);
    const [selectedBatch, setSelectedBatch] = useState<PackingBatchReady | null>(null);
    const [selectedItem, setSelectedItem] = useState<PackingBatchItem | null>(null);
    const [packingForm, setPackingForm] = useState<PackingCreate>({
        batch_id: 0,
        item_id: 0,
        source_grade: '',
        pack_size_kg: 0.5,
        expected_packs: 0,
        actual_packs: 0,
        total_weight_packed: 0,
        notes: ''
    });

    useEffect(() => {
        fetchData();
    }, [tabValue]);

    const fetchData = async () => {
        setLoading(true);
        try {
            if (tabValue === 0) {
                const data = await packingApi.getReadyBatches();
                setReadyBatches(data);
            } else {
                const data = await packingApi.getHistory();
                setHistory(data);
            }
            setError(null);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenPacking = (batch: PackingBatchReady, item: PackingBatchItem) => {
        setSelectedBatch(batch);
        setSelectedItem(item);
        const expected = Math.floor(Number(item.available_qty) / 0.5);
        setPackingForm({
            batch_id: batch.batch_id,
            item_id: item.item_id,
            source_grade: item.source_grade,
            pack_size_kg: 0.5,
            expected_packs: expected,
            actual_packs: expected,
            total_weight_packed: Number(item.available_qty),
            notes: ''
        });
        setOpenPackingDialog(true);
    };

    const handleRecordPacking = async () => {
        try {
            await packingApi.recordPacking(packingForm);
            enqueueSnackbar('Packing record saved successfully', { variant: 'success' });
            setOpenPackingDialog(false);
            fetchData();
        } catch (err: any) {
            enqueueSnackbar(err.response?.data?.detail || 'Failed to save record', { variant: 'error' });
        }
    };

    const handleFinalize = async (batchId: number) => {
        if (!window.confirm('Are you sure you want to finalize this batch? This will move items to inventory.')) return;
        try {
            await packingApi.finalizePacking(batchId);
            enqueueSnackbar('Batch finalized and moved to inventory', { variant: 'success' });
            fetchData();
        } catch (err: any) {
            enqueueSnackbar(err.response?.data?.detail || 'Failed to finalize batch', { variant: 'error' });
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Packing and Labeling
                </Typography>
                <IconButton onClick={fetchData} color="primary">
                    <LabelIcon />
                </IconButton>
            </Box>

            <Paper sx={{ mb: 3 }}>
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} indicatorColor="primary" textColor="primary">
                    <Tab icon={<PackingIcon />} label="Ready for Packing" iconPosition="start" />
                    <Tab icon={<HistoryIcon />} label="Packing History" iconPosition="start" />
                </Tabs>

                <TabPanel value={tabValue} index={0}>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                    ) : readyBatches.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="textSecondary">No batches ready for packing. Complete grading/GRN first.</Typography>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Batch # / GRN</TableCell>
                                        <TableCell>Vendor / Farm</TableCell>
                                        <TableCell>Grading Date</TableCell>
                                        <TableCell>Ready for Packing</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {readyBatches.map((batch) => (
                                        <TableRow key={batch.batch_id} hover sx={{ verticalAlign: 'top' }}>
                                            <TableCell sx={{ minWidth: 150 }}>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {batch.batch_number}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {batch.grn_number}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{batch.vendor_name}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {format(new Date(batch.grading_date), 'dd MMM yyyy')}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    {batch.items.map((item) => (
                                                        <Box
                                                            key={`${item.item_id}-${item.source_grade}`}
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                p: 0.5,
                                                                bgcolor: 'background.default',
                                                                borderRadius: 1,
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                minWidth: 250
                                                            }}
                                                        >
                                                            <Box>
                                                                <Typography variant="caption" fontWeight="bold" sx={{ mr: 1 }}>
                                                                    {item.item_name}
                                                                </Typography>
                                                                <Chip label={`Gr ${item.source_grade}`} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                                                            </Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    {Number(item.available_qty).toFixed(2)} kg
                                                                </Typography>
                                                                <Button
                                                                    size="small"
                                                                    variant="text"
                                                                    sx={{ minWidth: 0, p: '2px 8px' }}
                                                                    onClick={() => handleOpenPacking(batch, item)}
                                                                >
                                                                    Pack
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="success"
                                                    onClick={() => handleFinalize(batch.batch_id)}
                                                    startIcon={<FinalizeIcon />}
                                                >
                                                    Finalize
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Batch #</TableCell>
                                        <TableCell>Item</TableCell>
                                        <TableCell>Pack Size</TableCell>
                                        <TableCell>Quantity</TableCell>
                                        <TableCell>Overfill Wastage</TableCell>
                                        <TableCell>Staff</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {history.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell>{format(new Date(row.created_at), 'dd MMM HH:mm')}</TableCell>
                                            <TableCell>{row.batch_number}</TableCell>
                                            <TableCell>{row.item_name}</TableCell>
                                            <TableCell>{row.pack_size_kg} kg</TableCell>
                                            <TableCell>{row.actual_packs} units ({row.total_weight_packed} kg)</TableCell>
                                            <TableCell>
                                                {row.overfill_wastage_qty > 0 ? (
                                                    <Chip label={`${row.overfill_wastage_qty} kg`} size="small" color="warning" />
                                                ) : '0 kg'}
                                            </TableCell>
                                            <TableCell>{row.staff_name || 'N/A'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </TabPanel>
            </Paper>

            {/* Packing Dialog */}
            <Dialog open={openPackingDialog} onClose={() => setOpenPackingDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Record Packing: {selectedItem?.item_name} ({selectedBatch?.batch_number})
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Alert severity="info" icon={<CalcIcon />}>
                                    Available Bulk: <strong>{selectedItem?.available_qty} kg</strong> (Grade {selectedItem?.source_grade})
                                </Alert>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Pack Size (kg)"
                                    fullWidth
                                    type="number"
                                    value={packingForm.pack_size_kg}
                                    onChange={(e) => {
                                        const size = Number(e.target.value);
                                        const expected = selectedItem ? Math.floor(Number(selectedItem.available_qty) / size) : 0;
                                        setPackingForm({ ...packingForm, pack_size_kg: size, expected_packs: expected, actual_packs: expected });
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Expected Packs"
                                    fullWidth
                                    disabled
                                    value={packingForm.expected_packs}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Actual Packs"
                                    fullWidth
                                    type="number"
                                    value={packingForm.actual_packs}
                                    onChange={(e) => setPackingForm({ ...packingForm, actual_packs: Number(e.target.value) })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Total Gross Weight (kg)"
                                    fullWidth
                                    type="number"
                                    value={packingForm.total_weight_packed}
                                    onChange={(e) => setPackingForm({ ...packingForm, total_weight_packed: Number(e.target.value) })}
                                    helperText={`Available: ${selectedItem?.available_qty} kg`}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Notes"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={packingForm.notes}
                                    onChange={(e) => setPackingForm({ ...packingForm, notes: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="textSecondary">
                                        Theoretical Weight: {(packingForm.actual_packs * packingForm.pack_size_kg).toFixed(2)} kg
                                    </Typography>
                                    <Typography variant="body2" color={packingForm.total_weight_packed > (packingForm.actual_packs * packingForm.pack_size_kg) ? 'warning.main' : 'textSecondary'}>
                                        Calculated Overfill: {Math.max(0, packingForm.total_weight_packed - (packingForm.actual_packs * packingForm.pack_size_kg)).toFixed(2)} kg
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenPackingDialog(false)}>Cancel</Button>
                    <Button onClick={handleRecordPacking} variant="contained" color="primary">Save Record</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
